import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { toast } from "react-toastify";
import ConfirmSheet from "./ConfirmSheet";
import { useNavigate } from "react-router-dom";
import { withGuestSearch } from "data/guestPortalDefaults";
import { useGuestSession } from "contexts/GuestSessionContext";
import { LUXURY } from "data/guestLuxuryTheme";

const RequestForm = ({ catalog, type, locationLabel }) => {
  const navigate = useNavigate();
  const { createRequest, guestSearch } = useGuestSession();
  const [selectedId, setSelectedId] = useState(catalog?.items?.[0]?.id || "");
  const [notes, setNotes] = useState("");
  const [extraValue, setExtraValue] = useState("");
  const [confirm, setConfirm] = useState(null);

  const selected = useMemo(
    () => catalog?.items?.find((item) => item.id === selectedId),
    [catalog, selectedId]
  );

  const handleSubmit = () => {
    if (!selected) {
      toast.warning("Choose an option first");
      return;
    }
    const extra = selected.extraField
      ? `${selected.extraField.label}: ${extraValue || "anytime"}`
      : "";
    const details = [extra, notes].filter(Boolean).join(" · ");
    const created = createRequest({
      type,
      catalogItemId: selected.id,
      title: selected.title,
      details,
      etaMinutes: catalog.etaMinutes,
      locationLabel,
    });
    setConfirm({
      title: `${selected.title} requested`,
      message: `${selected.title} to ${locationLabel} — usually ${catalog.etaMinutes >= 60 ? `${Math.round(catalog.etaMinutes / 60)} hr` : `${catalog.etaMinutes} min`}.`,
      id: created.id,
    });
    setNotes("");
    setExtraValue("");
  };

  return (
    <>
      <Typography sx={{ fontSize: "0.86rem", color: LUXURY.muted, mb: 1.5, lineHeight: 1.5, wordBreak: "break-word" }}>
        {catalog?.whatHappensNext}
      </Typography>
      <Stack spacing={1.25}>
        {catalog?.items?.map((item) => {
          const active = item.id === selectedId;
          return (
            <Box
              key={item.id}
              component="button"
              onClick={() => setSelectedId(item.id)}
              sx={{
                appearance: "none",
                textAlign: "left",
                p: 1.5,
                minHeight: 56,
                borderRadius: "16px",
                border: `1px solid ${active ? LUXURY.gold : LUXURY.cardBorder}`,
                bgcolor: active ? alpha(LUXURY.gold, 0.12) : LUXURY.card,
                cursor: "pointer",
                boxShadow: active ? "0 10px 22px rgba(184,134,11,0.12)" : "none",
              }}
            >
              <Typography sx={{ fontWeight: 700, color: LUXURY.ink, lineHeight: 1.3, wordBreak: "break-word" }}>
                {item.title}
              </Typography>
              <Typography sx={{ mt: 0.4, fontSize: "0.8rem", color: LUXURY.muted, lineHeight: 1.4, wordBreak: "break-word" }}>
                {item.hint}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      {selected?.extraField?.type === "time" ? (
        <TextField
        type="time"
        label={selected.extraField.label}
        value={extraValue}
        onChange={(e) => setExtraValue(e.target.value)}
        fullWidth
        InputLabelProps={{ shrink: true }}
        sx={{
          mt: 2,
          "& .MuiOutlinedInput-root": {
            color: LUXURY.ink,
            bgcolor: LUXURY.cardSoft,
            borderRadius: "14px",
            "& fieldset": { borderColor: LUXURY.cardBorder },
          },
          "& .MuiInputLabel-root": { color: LUXURY.hint },
        }}
      />
      ) : null}

      {selected?.extraField?.type === "text" ? (
        <TextField
          label={selected.extraField.label}
          value={extraValue}
          onChange={(e) => setExtraValue(e.target.value)}
          fullWidth
          sx={{
            mt: 2,
            "& .MuiOutlinedInput-root": {
              color: LUXURY.ink,
              bgcolor: LUXURY.cardSoft,
              borderRadius: "14px",
              "& fieldset": { borderColor: LUXURY.cardBorder },
            },
            "& .MuiInputLabel-root": { color: LUXURY.hint },
          }}
        />
      ) : null}

      <TextField
        label="Add a note (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        fullWidth
        multiline
        minRows={2}
        sx={{
          mt: 2,
          "& .MuiOutlinedInput-root": {
            color: LUXURY.ink,
            bgcolor: LUXURY.cardSoft,
            borderRadius: "14px",
            "& fieldset": { borderColor: LUXURY.cardBorder },
          },
          "& .MuiInputLabel-root": { color: LUXURY.hint },
        }}
      />

      <Button
        fullWidth
        variant="contained"
        onClick={handleSubmit}
        sx={{
          mt: 2,
          minHeight: 48,
          fontWeight: 800,
          background: LUXURY.ctaGradient,
          color: LUXURY.forestDeep,
          fontSize: "0.95rem",
          borderRadius: "14px",
          boxShadow: LUXURY.ctaShadow,
          "&:hover": { background: LUXURY.ctaGradientHover },
        }}
      >
        Send request
      </Button>

      <ConfirmSheet
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        onClose={() => setConfirm(null)}
        onViewRequests={() => {
          setConfirm(null);
          navigate(withGuestSearch("/guest/requests", guestSearch));
        }}
      />
    </>
  );
};

export default RequestForm;
